import { useState } from "react";
import { useSelector } from "react-redux";
import { selectUser } from "store/src/slices/auth-slice";
import { Icon } from "components";
import { Button } from "ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "ui/components/dialog";

// Sync button that confirms in a modal before calling onSync. Used for the global and the repository sync.
// onSync must return a promise (e.g. mutation.mutateAsync); the modal closes when it resolves.
export default function SyncButton({ onSync, isSyncing, title, description }) {
  const [open, setOpen] = useState(false);
  // Only job coordinators can sync (same rule as "Reset here" on the branch activity page).
  const canSync = !!useSelector(selectUser)?.role?.is_job_coordinator;
  if (!canSync) return null;
  return (
    <>
      <Button variant="outline" disabled={isSyncing} onClick={() => setOpen(true)}>
        <Icon icon="lucide:refresh-cw" className={isSyncing ? "size-4 animate-spin" : "size-4"} />
        {isSyncing ? "Syncing..." : "Sync"}
      </Button>
      {/* Stays open (and can't be dismissed) until the API responds. */}
      <Dialog open={open} onOpenChange={(next) => !isSyncing && setOpen(next)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>{description}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" disabled={isSyncing} onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={isSyncing}
              onClick={async () => {
                try {
                  await onSync();
                  setOpen(false);
                } catch {
                  // The caller's onError shows the toast; keep the modal open to retry or cancel.
                }
              }}
            >
              {isSyncing && <Icon icon="lucide:loader-circle" className="size-4 animate-spin" />}
              {isSyncing ? "Syncing..." : "Sync"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
